import React, { useState, useEffect, useCallback } from "react";
import { FaWhatsapp, FaTimes } from "react-icons/fa";
import axios from "axios";
import { getUserProfile, updateUserProfile } from "../../services/userAuthService";
import "./MobileNumberPrompt.css";

const DISMISS_KEY = "mobilePromptDismissedAt";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const MobileNumberPrompt = () => {
 const [visible, setVisible] = useState(false);
 const [mobileNumber, setMobileNumber] = useState("");
 const [dialCode, setDialCode] = useState("+91");
 const [countryCodes, setCountryCodes] = useState([]);
 const [whatsappConsent, setWhatsappConsent] = useState(false);
 const [error, setError] = useState("");
 const [loading, setLoading] = useState(false);
 const [success, setSuccess] = useState(false);

 useEffect(() => {
 axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
 .then(res => setCountryCodes(res.data?.data || []))
 .catch(() => {});
 }, []);

 const checkAndShow = useCallback(async () => {
 // Only for logged-in users
 const token = localStorage.getItem("token");
 if (!token) return;

 // Check 24hr dismiss
 const dismissedAt = localStorage.getItem(DISMISS_KEY);
 if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION_MS) return;

 try {
 const res = await getUserProfile();
 const profile = res.data;
 const mobile = profile?.mobileNumber || profile?.mobile_number;
 if (!mobile) {
 setVisible(true);
 }
 } catch (err) {
 // Silently fail — don't block homepage
 }
 }, []);

 useEffect(() => {
 checkAndShow();
 }, [checkAndShow]);

 const handleDismiss = () => {
 localStorage.setItem(DISMISS_KEY, Date.now().toString());
 setVisible(false);
 };

 const handleSave = async () => {
 setError("");

 if (!mobileNumber) {
 setError("Please enter your mobile number");
 return;
 }

 const fullNumber = dialCode + mobileNumber;
 const e164Regex = /^\+[1-9]\d{1,14}$/;
 if (!e164Regex.test(fullNumber)) {
 setError("Please enter a valid mobile number");
 return;
 }

 setLoading(true);
 try {
 await updateUserProfile({
 mobile_number: fullNumber,
 whatsapp_enabled: whatsappConsent,
 });
 setSuccess(true);
 setTimeout(() => setVisible(false), 1500);
 } catch (err) {
 const raw = err.response?.data?.message || "";
 setError(
 raw.includes("INTERNAL_ERROR") || raw.includes("cannot be cast")
 ? "Something went wrong. Please try again later."
 : raw || "Failed to save. Please try again."
 );
 } finally {
 setLoading(false);
 }
 };

 if (!visible) return null;

 return (
 <div className="mobile-prompt-banner">
 <button className="mobile-prompt-close" onClick={handleDismiss} aria-label="Dismiss">
 <FaTimes />
 </button>

 {success ? (
 <div className="mobile-prompt-success">
 <FaWhatsapp style={{ color: "#25D366", fontSize: 20 }} />
 <span>Mobile number saved successfully!</span>
 </div>
 ) : (
 <>
 <div className="mobile-prompt-header">
 <FaWhatsapp style={{ color: "#25D366", fontSize: 22 }} />
 <span>Add your WhatsApp number for session reminders & updates</span>
 </div>

 <div className="mobile-prompt-body">
 <div className="mobile-prompt-input-row">
 <select
 className="mobile-prompt-country-select"
 value={dialCode}
 onChange={(e) => setDialCode(e.target.value)}
 >
 {countryCodes.length > 0 ? (
 countryCodes.map((c) => (
 <option key={c.id} value={c.dialCode}>
 {c.isoCode} {c.dialCode}
 </option>
 ))
 ) : (
 <option value="+91">IN +91</option>
 )}
 </select>
 <input
 type="tel"
 value={mobileNumber}
 onChange={(e) => {
 setMobileNumber(e.target.value.replace(/[^\d]/g, ""));
 setError("");
 }}
 placeholder="0000000000"
 maxLength={15}
 className="mobile-prompt-input"
 />
 <button
 className="mobile-prompt-save-btn"
 onClick={handleSave}
 disabled={loading}
 >
 {loading ? "Saving..." : "Save"}
 </button>
 </div>

 <label className="mobile-prompt-consent">
 <input
 type="checkbox"
 checked={whatsappConsent}
 onChange={(e) => setWhatsappConsent(e.target.checked)}
 />
 <span>I agree to receive WhatsApp notifications</span>
 </label>

 {error && <p className="mobile-prompt-error">{error}</p>}
 </div>
 </>
 )}
 </div>
 );
};

export default MobileNumberPrompt;
